import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ReservationModule() {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    typeTrajet: 'transfert-simple',
    adresseDepart: '',
    adresseArrivee: '',
    date: '',
    heure: '',
    horaireType: 'jour',
    passagers: '1',
    bagages: '2',
    skis: 'oui',
    vehicule: 'classe-e',
    commentaires: '',
    prixCalcule: '0',
    detailPrix: '',
    departLat: '',
    departLng: '',
    arriveeLat: '',
    arriveeLng: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [distance, setDistance] = useState(0);
  const [priceBreakdown, setPriceBreakdown] = useState({
    basePrice: 0,
    nightSurcharge: 0,
    vanSurcharge: 0,
    total: 0
  });

  const departInputRef = useRef<HTMLInputElement>(null);
  const arriveeInputRef = useRef<HTMLInputElement>(null);
  const autocompleteDepart = useRef<any>(null);
  const autocompleteArrivee = useRef<any>(null);

  useEffect(() => {
    const loadGoogleMaps = () => {
      // Vérifier si Google Maps est déjà chargé
      if ((window as any).google?.maps?.places) {
        initAutocomplete();
        return;
      }

      // Vérifier si le script est déjà en cours de chargement
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          if ((window as any).google?.maps?.places) {
            initAutocomplete();
          }
        });
        return;
      }

      // Charger le script Google Maps
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDqTcxBYZyFNdjkYV4ExiNzz64elAkv5wE&libraries=places&language=fr`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        if ((window as any).google?.maps?.places) {
          initAutocomplete();
        }
      };

      script.onerror = () => {
        console.error('Erreur lors du chargement de Google Maps');
      };

      document.head.appendChild(script);
    };

    // Attendre que les refs soient disponibles
    const timer = setTimeout(() => {
      if (departInputRef.current && arriveeInputRef.current) {
        loadGoogleMaps();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const initAutocomplete = () => {
    if (!departInputRef.current || !arriveeInputRef.current) return;
    if (!(window as any).google?.maps?.places) return;

    try {
      const google = (window as any).google;

      // Nettoyer les anciennes instances
      if (autocompleteDepart.current) {
        google.maps.event.clearInstanceListeners(departInputRef.current);
      }
      if (autocompleteArrivee.current) {
        google.maps.event.clearInstanceListeners(arriveeInputRef.current);
      }

      // Autocomplete pour le départ
      autocompleteDepart.current = new google.maps.places.Autocomplete(departInputRef.current, {
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: ['fr', 'ch', 'it'] },
        fields: ['formatted_address', 'geometry', 'name', 'address_components']
      });

      autocompleteDepart.current.addListener('place_changed', () => {
        const place = autocompleteDepart.current?.getPlace();
        if (place?.geometry?.location) {
          const address = place.formatted_address || place.name || '';
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          setFormData(prev => ({
            ...prev,
            adresseDepart: address,
            departLat: lat.toString(),
            departLng: lng.toString()
          }));
          
          // Calculer la distance si l'arrivée est déjà renseignée
          if (formData.arriveeLat && formData.arriveeLng) {
            setTimeout(() => {
              calculateDistanceWithCoords(lat, lng, parseFloat(formData.arriveeLat), parseFloat(formData.arriveeLng));
            }, 100);
          }
        }
      });

      // Autocomplete pour l'arrivée
      autocompleteArrivee.current = new google.maps.places.Autocomplete(arriveeInputRef.current, {
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: ['fr', 'ch', 'it'] },
        fields: ['formatted_address', 'geometry', 'name', 'address_components']
      });

      autocompleteArrivee.current.addListener('place_changed', () => {
        const place = autocompleteArrivee.current?.getPlace();
        if (place?.geometry?.location) {
          const address = place.formatted_address || place.name || '';
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          
          setFormData(prev => ({
            ...prev,
            adresseArrivee: address,
            arriveeLat: lat.toString(),
            arriveeLng: lng.toString()
          }));
          
          // Calculer la distance si le départ est déjà renseigné
          if (formData.departLat && formData.departLng) {
            setTimeout(() => {
              calculateDistanceWithCoords(parseFloat(formData.departLat), parseFloat(formData.departLng), lat, lng);
            }, 100);
          }
        }
      });
    } catch (error) {
      console.error('Erreur lors de l\'initialisation de l\'autocomplétion:', error);
    }
  };

  // Nouvelle fonction pour calculer la distance avec des coordonnées spécifiques
  const calculateDistanceWithCoords = (departLat: number, departLng: number, arriveeLat: number, arriveeLng: number) => {
    if (!(window as any).google?.maps) {
      console.error('Google Maps n\'est pas chargé');
      return;
    }

    try {
      const google = (window as any).google;
      const service = new google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [{ lat: departLat, lng: departLng }],
          destinations: [{ lat: arriveeLat, lng: arriveeLng }],
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.METRIC,
        },
        (response: any, status: string) => {
          if (status === 'OK' && response?.rows[0]?.elements[0]?.status === 'OK') {
            const distanceInKm = response.rows[0].elements[0].distance.value / 1000;
            setDistance(Math.round(distanceInKm));
            calculatePrice(distanceInKm);
          } else {
            console.error('Erreur lors du calcul de la distance:', status);
          }
        }
      );
    } catch (error) {
      console.error('Erreur lors du calcul de la distance:', error);
    }
  };

  const calculateDistance = () => {
    if (!formData.departLat || !formData.departLng || !formData.arriveeLat || !formData.arriveeLng) {
      return;
    }

    calculateDistanceWithCoords(
      parseFloat(formData.departLat),
      parseFloat(formData.departLng),
      parseFloat(formData.arriveeLat),
      parseFloat(formData.arriveeLng)
    );
  };

  const calculatePrice = (distanceKm: number) => {
    let basePrice = Math.round(distanceKm * 2.5);
    if (basePrice < 80) basePrice = 80;

    let nightSurcharge = 0;
    let vanSurcharge = 0;

    if (formData.horaireType === 'nuit') {
      nightSurcharge = Math.round(basePrice * 0.2);
    }

    if (formData.vehicule === 'van') {
      vanSurcharge = Math.round(basePrice * 0.25);
    }

    const total = basePrice + nightSurcharge + vanSurcharge;
    setCalculatedPrice(total);
    setPriceBreakdown({
      basePrice,
      nightSurcharge,
      vanSurcharge,
      total
    });

    let detailPrix = `Distance: ${Math.round(distanceKm)}km • Tarif de base: ${basePrice}€`;
    if (vanSurcharge > 0) {
      detailPrix += ` + Supplément Van (+25%): ${vanSurcharge}€`;
    }
    if (nightSurcharge > 0) {
      detailPrix += ` + Supplément nuit (+20%): ${nightSurcharge}€`;
    }
    detailPrix += ` = Total: ${total}€`;

    setFormData(prev => ({
      ...prev,
      prixCalcule: total.toString(),
      detailPrix: detailPrix
    }));
  };

  useEffect(() => {
    if (distance > 0) {
      calculatePrice(distance);
    }
  }, [formData.vehicule, formData.horaireType, distance]);

  const validateField = (name: string, value: string) => {
    const newErrors = { ...errors };
    
    switch (name) {
      case 'nom':
        if (value.trim().length < 2) {
          newErrors[name] = 'Le nom doit contenir au moins 2 caractères';
        } else {
          delete newErrors[name];
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          newErrors[name] = 'Email invalide';
        } else {
          delete newErrors[name];
        }
        break;
      case 'telephone':
        const cleanPhone = value.replace(/\s/g, '');
        if (cleanPhone.length < 8) {
          newErrors[name] = 'Numéro de téléphone invalide';
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    
    setErrors(newErrors);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    validateField(name, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Valider tous les champs requis
    const requiredFields = ['nom', 'email', 'telephone', 'adresseDepart', 'adresseArrivee', 'date', 'heure'];
    const newErrors: Record<string, string> = {};
    
    requiredFields.forEach(field => {
      if (!formData[field as keyof typeof formData] || formData[field as keyof typeof formData].trim() === '') {
        newErrors[field] = 'Ce champ est requis';
      }
    });

    // Valider les champs spécifiques
    if (formData.nom.trim().length < 2) {
      newErrors.nom = 'Le nom doit contenir au moins 2 caractères';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    
    const cleanPhone = formData.telephone.replace(/\s/g, '');
    if (cleanPhone.length < 8) {
      newErrors.telephone = 'Numéro de téléphone invalide';
    }

    if (!formData.departLat || !formData.departLng) {
      newErrors.adresseDepart = 'Veuillez sélectionner une adresse dans la liste';
    }

    if (!formData.arriveeLat || !formData.arriveeLng) {
      newErrors.adresseArrivee = 'Veuillez sélectionner une adresse dans la liste';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSubmitError('Veuillez corriger les erreurs dans le formulaire');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const formBody = new URLSearchParams();
      
      // Préparer les données avec des labels clairs
      const dataToSend = {
        'Nom complet': formData.nom,
        'Email': formData.email,
        'Téléphone': formData.telephone,
        'Type de trajet': formData.typeTrajet === 'transfert-simple' ? 'Transfert aller simple' : 'Mise à disposition',
        'Adresse de départ': formData.adresseDepart,
        'Adresse d\'arrivée': formData.adresseArrivee,
        'Date': formData.date,
        'Heure': formData.heure,
        'Horaire': formData.horaireType === 'jour' ? 'Jour (7h-19h)' : 'Nuit (19h-7h) +20%',
        'Nombre de passagers': formData.passagers,
        'Nombre de bagages': formData.bagages,
        'Équipement ski': formData.skis === 'oui' ? 'Oui' : 'Non',
        'Véhicule': formData.vehicule === 'classe-e' ? 'Mercedes Classe E (jusqu\'à 3 passagers)' : 'Mercedes Van (jusqu\'à 7 passagers) +25%',
        'Commentaires': formData.commentaires || 'Aucun',
        'Prix calculé': formData.prixCalcule ? `${formData.prixCalcule}€` : 'Non calculé',
        'Détail du prix': formData.detailPrix || 'Non disponible'
      };

      Object.entries(dataToSend).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5mgaa0lhqshf1uh0fn0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      // Succès
      setShowSuccess(true);
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        typeTrajet: 'transfert-simple',
        adresseDepart: '',
        adresseArrivee: '',
        date: '',
        heure: '',
        horaireType: 'jour',
        passagers: '1',
        bagages: '2',
        skis: 'oui',
        vehicule: 'classe-e',
        commentaires: '',
        prixCalcule: '0',
        detailPrix: '',
        departLat: '',
        departLng: '',
        arriveeLat: '',
        arriveeLng: ''
      });
      setCalculatedPrice(0);
      setDistance(0);
      setErrors({});
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      setTimeout(() => setShowSuccess(false), 8000);
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setSubmitError('Une erreur est survenue lors de l\'envoi. Veuillez réessayer ou nous contacter sur WhatsApp.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fonction pour générer le message WhatsApp
  const generateWhatsAppMessage = () => {
    const typeTrajetLabel = formData.typeTrajet === 'transfert-simple' 
      ? 'Transfert aller simple' 
      : 'Mise à disposition';

    const vehiculeLabel = formData.vehicule === 'classe-e' 
      ? 'Mercedes Classe E (jusqu\'à 3 passagers)' 
      : 'Mercedes Van (jusqu\'à 7 passagers)';

    const horaireLabel = formData.horaireType === 'jour' 
      ? 'Jour (7h-19h)' 
      : 'Nuit (19h-7h)';

    let message = `🚗 *Demande de réservation*\n\n`;
    
    if (formData.nom) {
      message += `👤 *Nom :* ${formData.nom}\n`;
    }
    if (formData.telephone) {
      message += `📞 *Téléphone :* ${formData.telephone}\n`;
    }
    if (formData.email) {
      message += `📧 *Email :* ${formData.email}\n`;
    }
    
    message += `\n🔄 *Type de trajet :* ${typeTrajetLabel}\n`;
    message += `📍 *Départ :* ${formData.adresseDepart || 'À définir'}\n`;
    message += `📍 *Arrivée :* ${formData.adresseArrivee || 'À définir'}\n`;
    message += `📅 *Date :* ${formData.date || 'À définir'}\n`;
    message += `🕐 *Heure :* ${formData.heure || 'À définir'}\n`;
    message += `⏰ *Horaire :* ${horaireLabel}\n`;
    message += `\n🚙 *Véhicule :* ${vehiculeLabel}\n`;
    message += `👥 *Passagers :* ${formData.passagers}\n`;
    message += `🧳 *Bagages :* ${formData.bagages}\n`;
    message += `⛷️ *Équipement ski :* ${formData.skis === 'oui' ? 'Oui' : 'Non'}\n`;
    
    if (calculatedPrice > 0) {
      message += `\n💰 *Prix estimé :* ${calculatedPrice}€`;
      if (distance > 0) {
        message += ` (${distance}km)`;
      }
      message += `\n`;
    }

    if (formData.commentaires) {
      message += `\n💬 *Commentaires :*\n${formData.commentaires}\n`;
    }

    return encodeURIComponent(message);
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-slate-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-0 left-0 w-96 h-96 bg-alpine-gold rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 items-start mb-12">
          {/* Titre et description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-alpine-gold to-amber-600 rounded-2xl mb-6 shadow-xl"
            >
              <i className="ri-calendar-check-line text-3xl text-white"></i>
            </motion.div>
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-night-blue mb-4">
              Réservez Votre Transfert
            </h2>
            <p className="font-inter text-lg text-slate-600">
              Calculez instantanément le prix de votre trajet et réservez en quelques clics
            </p>
          </motion.div>

          {/* Module de prix fixe */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-voyage-gold via-amber-500 to-voyage-gold rounded-3xl p-8 shadow-2xl relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 bg-night-blue/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-night-blue font-inter text-sm font-bold">Prix calculé en temps réel</span>
              </div>
              
              <AnimatePresence mode="wait">
                {calculatedPrice > 0 ? (
                  <motion.div
                    key={calculatedPrice}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 200 }}
                  >
                    <div className="font-playfair text-6xl md:text-7xl font-bold text-night-blue mb-2 drop-shadow-lg">
                      {calculatedPrice}€
                    </div>
                    <div className="text-night-blue font-inter text-lg mb-6 font-semibold">
                      Prix fixe tout compris • Sans frais cachés
                    </div>
                    
                    {/* Détail du prix avec suppléments */}
                    {(priceBreakdown.vanSurcharge > 0 || priceBreakdown.nightSurcharge > 0) && (
                      <div className="bg-night-blue/10 backdrop-blur-sm rounded-xl p-4 mb-6 space-y-2">
                        <div className="flex justify-between items-center text-night-blue">
                          <span className="font-inter text-sm">Tarif de base</span>
                          <span className="font-inter font-bold">{priceBreakdown.basePrice}€</span>
                        </div>
                        {priceBreakdown.vanSurcharge > 0 && (
                          <div className="flex justify-between items-center text-night-blue">
                            <span className="font-inter text-sm">Supplément Van (+25%)</span>
                            <span className="font-inter font-bold">+{priceBreakdown.vanSurcharge}€</span>
                          </div>
                        )}
                        {priceBreakdown.nightSurcharge > 0 && (
                          <div className="flex justify-between items-center text-night-blue">
                            <span className="font-inter text-sm">Supplément nuit (+20%)</span>
                            <span className="font-inter font-bold">+{priceBreakdown.nightSurcharge}€</span>
                          </div>
                        )}
                        <div className="border-t border-night-blue/20 pt-2 mt-2">
                          <div className="flex justify-between items-center text-night-blue">
                            <span className="font-inter font-bold">Total</span>
                            <span className="font-playfair text-2xl font-bold">{priceBreakdown.total}€</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <div className="font-playfair text-5xl font-bold text-night-blue/60 mb-2">
                      ---€
                    </div>
                    <div className="text-night-blue/60 font-inter text-lg mb-6 font-semibold">
                      Remplissez le formulaire pour voir le prix
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: 'ri-check-line', text: 'Bagages inclus' },
                  { icon: 'ri-check-line', text: 'Équipement ski' },
                  { icon: 'ri-check-line', text: 'WiFi gratuit' },
                  { icon: 'ri-check-line', text: 'Eau offerte' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + 0.1 * index }}
                    className="flex items-center gap-2 text-night-blue text-sm font-medium"
                  >
                    <i className={`${item.icon} text-green-600`}></i>
                    <span>{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <AnimatePresence>
          {showSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className="mb-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 sm:p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring' }}
                    className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center"
                  >
                    <i className="ri-check-line text-4xl"></i>
                  </motion.div>
                  <div>
                    <h3 className="font-playfair text-2xl font-bold mb-1">Réservation confirmée !</h3>
                    <p className="text-green-50">Votre demande a été envoyée avec succès</p>
                  </div>
                </div>
                <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <p className="text-sm leading-relaxed">
                    <strong>Prochaines étapes :</strong><br/>
                    • Vous recevrez un email de confirmation dans quelques minutes<br/>
                    • Notre équipe vous contactera sous 2h pour finaliser les détails<br/>
                    • Un SMS de rappel vous sera envoyé 24h avant votre transfert
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {submitError && (
            <motion.div
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              className="mb-8 bg-gradient-to-r from-red-500 to-rose-500 rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="p-6 sm:p-8 text-white">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-line text-3xl"></i>
                  </div>
                  <div>
                    <h3 className="font-playfair text-xl font-bold mb-1">Erreur</h3>
                    <p className="text-red-50 text-sm">{submitError}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-br from-night-blue to-slate-900 rounded-3xl p-12">
            <h3 className="font-playfair text-3xl lg:text-4xl font-bold text-white mb-4">
              Prêt à Réserver Votre Transfert ?
            </h3>
            <p className="text-slate-300 font-inter text-lg mb-8">
              Service disponible 24/7 • Réservation instantanée • Tarifs transparents
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`https://wa.me/33605606006?text=${generateWhatsAppMessage()}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-green-500 text-white rounded-xl font-inter font-medium hover:bg-green-600 transition-all shadow-lg whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                Réserver sur WhatsApp
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
